import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Ruler, Bed, MapPin, Buildings, Warehouse, Storefront, HouseLine, Briefcase } from "@phosphor-icons/react";
import { useT } from "../i18n";
import { useRate } from "../context/RateContext";
import { useAuth } from "../context/AuthContext";
import { formatMAD, convertMad } from "../lib/format";
import { toast } from "sonner";

const typeIcons = {
  residence: Buildings,
  individual: HouseLine,
  commercial: Storefront,
  office: Briefcase,
  logistics: Warehouse,
  new_build: Buildings,
};

const PropertyCard = ({ property, index = 0 }) => {
  const { t, lang } = useT();
  const rate = useRate();
  const { user, toggleFavorite } = useAuth();
  const navigate = useNavigate();

  const title = property.title?.[lang] || property.title?.fr || "";
  const Icon = typeIcons[property.type] || Buildings;
  const conv = convertMad(property.price_mad, rate, lang);
  const isFav = user && user.favorites && user.favorites.includes(property.id);

  const onFav = async (e) => {
    e.stopPropagation();
    if (!user || !user.email) { navigate("/connexion"); return; }
    const res = await toggleFavorite(property.id);
    toast.success(res.active ? "Ajouté à vos favoris" : "Retiré de vos favoris");
  };

  return (
    <div
      data-testid={`property-card-${property.id}`}
      onClick={() => navigate(`/biens/${property.id}`)}
      className="group cursor-pointer bg-white border border-navy/10 hover:border-gold/60 transition-colors duration-300 overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={property.images?.[0]}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-3 start-3 flex items-center gap-1.5 bg-navy/90 text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5">
          <Icon size={14} weight="light" /> {t(`types.${property.type}`)}
        </div>
        <button
          data-testid={`fav-toggle-${property.id}`}
          onClick={onFav}
          className="absolute top-3 end-3 w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-white transition-colors rounded-full"
        >
          <Heart size={18} weight={isFav ? "fill" : "regular"} className={isFav ? "text-red-500" : "text-navy"} />
        </button>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
          <MapPin size={14} weight="light" /> {property.city}
        </div>
        <h3 className="font-display text-xl text-navy leading-snug mb-3 line-clamp-2">{title}</h3>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          {property.surface_m2 > 0 && <span className="flex items-center gap-1"><Ruler size={16} weight="light" /> <span className="font-roboto font-semibold text-navy">{property.surface_m2}</span> m²</span>}
          {property.bedrooms > 0 && <span className="flex items-center gap-1"><Bed size={16} weight="light" /> <span className="font-roboto font-semibold text-navy">{property.bedrooms}</span></span>}
        </div>

        <div className="mt-auto pt-4 border-t border-navy/5">
          {property.price_on_request || !property.price_mad ? (
            <div className="font-roboto font-bold text-xl text-navy py-1.5">{t("prop.on_request")}</div>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("prop.from")}</div>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-roboto font-bold text-2xl text-navy">{formatMAD(property.price_mad, lang)}</span>
                <span className="text-sm text-muted-foreground">{t("common.mad")}</span>
              </div>
              <div className="text-sm text-gold font-roboto font-semibold">≈ {conv.text} {conv.symbol}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
